import { useCallback, useEffect, useRef, useState } from 'react'
import {
  Alert,
  Button,
  Modal,
  Popconfirm,
  Progress,
  Table,
  Tag,
  Upload,
  message,
} from 'antd'
import type { UploadProps } from 'antd'
import {
  CloudUploadOutlined,
  DeleteOutlined,
  EyeOutlined,
  FileOutlined,
  HistoryOutlined,
  ReloadOutlined,
  SafetyCertificateOutlined,
} from '@ant-design/icons'
import { api, formatApiError } from '../api'
import { DocDesensitizeViewer } from '../components/DocDesensitizeViewer'
import { formatDate, PageHeader, tablePagination, useServerPagination } from '../components/ui'
import './DocCompare.css'

const POLL_MS = 2000

type UploadedFile = {
  itemId: number
  title: string
}

type JobItem = {
  id: number
  status: string
  progress: number
  status_message: string
  file_name: string
  hit_count: number
  similarity_percent?: number | null
  diff_percent?: number | null
  error_message?: string | null
  created_at: string | null
  finished_at: string | null
}

type JobDetail = JobItem & {
  result?: {
    hits: { rule_code: string; text: string }[]
    hit_count: number
    truncated?: boolean
    has_masked_pdf?: boolean
  }
}

const STATUS_COLOR: Record<string, string> = {
  pending: 'default',
  running: 'processing',
  completed: 'success',
  error: 'error',
}

const STATUS_LABEL: Record<string, string> = {
  pending: '等待中',
  running: '处理中',
  completed: '已完成',
  error: '失败',
}

export default function DocDesensitize() {
  const [uploading, setUploading] = useState(false)
  const [running, setRunning] = useState(false)
  const [uploaded, setUploaded] = useState<UploadedFile | null>(null)
  const [listLoading, setListLoading] = useState(false)
  const [jobs, setJobs] = useState<JobItem[]>([])
  const [total, setTotal] = useState(0)
  const [viewJob, setViewJob] = useState<JobDetail | null>(null)
  const [activeJobId, setActiveJobId] = useState<number | null>(null)
  const [deletingJobId, setDeletingJobId] = useState<number | null>(null)
  const { page, pageSize, handlePaginationChange, paginationOptions, resetPage } = useServerPagination()
  const historyRef = useRef<HTMLDivElement>(null)

  const loadJobs = useCallback(
    (pageOverride?: number) => {
      const p = pageOverride ?? page
      setListLoading(true)
      return api
        .get<{ items: JobItem[]; total: number }>('/public/desensitize/jobs', {
          params: { page: p, page_size: pageSize },
        })
        .then((r) => {
          setJobs(r.data.items || [])
          setTotal(r.data.total || 0)
        })
        .catch((e) => message.error(formatApiError(e, '加载任务列表失败')))
        .finally(() => setListLoading(false))
    },
    [page, pageSize],
  )

  const refreshJob = useCallback((jobId: number) => {
    return api
      .get<JobDetail>(`/public/desensitize/jobs/${jobId}`)
      .then((r) => {
        setViewJob(r.data)
        return r.data
      })
      .catch((e) => {
        message.error(formatApiError(e, '加载任务详情失败'))
        return null
      })
  }, [])

  useEffect(() => {
    void loadJobs()
  }, [loadJobs])

  useEffect(() => {
    const tracking =
      activeJobId != null || jobs.some((j) => j.status === 'pending' || j.status === 'running')
    if (!tracking) return

    const tick = () => {
      void loadJobs()
      if (activeJobId != null) {
        void refreshJob(activeJobId).then((job) => {
          if (!job) return
          if (job.status === 'completed') {
            setRunning(false)
            setActiveJobId(null)
            message.success(`「${job.file_name}」脱敏完成，共命中 ${job.hit_count} 处`)
          } else if (job.status === 'error') {
            setRunning(false)
            setActiveJobId(null)
            message.error(job.error_message || '脱敏失败')
          }
        })
      }
    }

    const t = window.setInterval(tick, POLL_MS)
    return () => window.clearInterval(t)
  }, [activeJobId, jobs, loadJobs, refreshJob])

  const uploadPdfFile = useCallback((file: File) => {
    setUploading(true)
    const fd = new FormData()
    fd.append('file', file)
    api
      .post<{ item_id: number; title: string; message: string }>('/public/desensitize/upload', fd)
      .then((r) => {
        setUploaded({ itemId: r.data.item_id, title: r.data.title })
        setViewJob(null)
        setActiveJobId(null)
        setRunning(false)
        message.success(r.data.message || '上传成功')
      })
      .catch((e) => message.error(formatApiError(e, '上传失败')))
      .finally(() => setUploading(false))
  }, [])

  const uploadProps: UploadProps = {
    accept: '.pdf,application/pdf',
    showUploadList: false,
    multiple: false,
    beforeUpload: (file) => {
      uploadPdfFile(file)
      return false
    },
  }

  const runDesensitize = () => {
    if (!uploaded?.itemId) return
    setRunning(true)
    setViewJob(null)
    api
      .post<{ job_id: number; message: string }>('/public/desensitize/run', {
        item_id: uploaded.itemId,
      })
      .then(async (r) => {
        const jobId = r.data.job_id
        setActiveJobId(jobId)
        resetPage()
        await loadJobs(1)
        await refreshJob(jobId)
        historyRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      })
      .catch((e) => {
        message.error(formatApiError(e, '提交脱敏失败'))
        setRunning(false)
      })
  }

  const openJob = (row: JobItem) => {
    void refreshJob(row.id)
  }

  const deleteJob = (id: number) => {
    setDeletingJobId(id)
    api
      .delete(`/public/desensitize/jobs/${id}`)
      .then(() => {
        if (viewJob?.id === id) setViewJob(null)
        if (activeJobId === id) {
          setActiveJobId(null)
          setRunning(false)
        }
        message.success('已删除脱敏任务')
        const nextPage = jobs.length <= 1 && page > 1 ? page - 1 : page
        if (nextPage !== page) handlePaginationChange(nextPage, pageSize)
        else void loadJobs(nextPage)
      })
      .catch((e) => message.error(formatApiError(e, '删除失败')))
      .finally(() => setDeletingJobId(null))
  }

  const backFromViewer = () => {
    setViewJob(null)
  }

  const showResult = viewJob?.status === 'completed'

  const showProgress =
    viewJob &&
    (viewJob.status === 'pending' || viewJob.status === 'running' || viewJob.status === 'error')

  const ready = !!uploaded?.itemId

  return (
    <div className="doc-compare-page public-page-scroll">
      <PageHeader
        title="文档脱敏"
        description="上传 PDF 后点击「开始脱敏」，识别姓名、住址、手机号、身份证号、银行卡号等敏感信息，并以 PDF 对照查看脱敏前后效果。"
      />

      <div className="dc-hero">
        <div className="dc-hero-icon">
          <SafetyCertificateOutlined />
        </div>
        <div className="dc-hero-text">
          <span className="dc-hero-title">上传 PDF，确认后开始脱敏</span>
          <span className="dc-hero-desc">当前版本仅支持 PDF；Word / Excel 等格式后续开放</span>
        </div>
      </div>

      <div className="dc-slots" style={{ gridTemplateColumns: '1fr' }}>
        <div className="dc-slot dc-slot--left">
          <div className="dc-slot-head">
            <div className="dc-slot-label">
              <span className="dc-slot-dot" />
              待脱敏文档
            </div>
          </div>
          <div className="dc-slot-body">
            <div className="dc-slot-content">
              {uploaded ? (
                <div className="dc-file-card">
                  <div className="dc-file-card-icon">
                    <FileOutlined />
                  </div>
                  <span className="dc-file-card-name">{uploaded.title}</span>
                  <span className="dc-file-card-id">文档 ID {uploaded.itemId}</span>
                  <div className="dc-file-card-actions">
                    <Upload {...uploadProps} disabled={uploading || running}>
                      <Button
                        size="small"
                        icon={<ReloadOutlined />}
                        loading={uploading}
                        disabled={running}
                      >
                        重新选择
                      </Button>
                    </Upload>
                  </div>
                </div>
              ) : (
                <Upload.Dragger {...uploadProps} disabled={uploading}>
                  <p className="ant-upload-drag-icon">
                    <CloudUploadOutlined />
                  </p>
                  <p className="ant-upload-text">{uploading ? '正在上传…' : '点击或拖拽 PDF 到此处'}</p>
                  <p className="ant-upload-hint">仅上传，不会立即脱敏</p>
                </Upload.Dragger>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="dc-action-bar">
        <Button
          type="primary"
          className="dc-run-btn"
          icon={<SafetyCertificateOutlined />}
          loading={running}
          disabled={!ready || uploading}
          onClick={runDesensitize}
        >
          开始脱敏
        </Button>
        <span className="dc-action-hint">
          {running
            ? '正在脱敏，请稍候…'
            : ready
              ? '文档已就绪，点击开始脱敏'
              : '请先上传 PDF 文件'}
        </span>
      </div>

      {showProgress && viewJob && (
        <Alert
          type={viewJob.status === 'error' ? 'error' : 'info'}
          showIcon
          style={{ marginBottom: 16 }}
          message={`任务 #${viewJob.id} · ${viewJob.file_name}`}
          description={
            viewJob.status === 'error' ? (
              viewJob.error_message || '脱敏失败'
            ) : (
              <Progress
                percent={viewJob.progress}
                status={viewJob.status === 'pending' ? 'normal' : 'active'}
                format={() => viewJob.status_message || STATUS_LABEL[viewJob.status]}
              />
            )
          }
          action={
            viewJob.status === 'completed' ? (
              <Button type="primary" size="small" onClick={() => openJob(viewJob)}>
                查看对照
              </Button>
            ) : viewJob.status === 'error' ? (
              <Button size="small" onClick={() => setViewJob(null)}>
                关闭
              </Button>
            ) : null
          }
        />
      )}

      <div className="dc-history" ref={historyRef}>
        <div className="dc-history-head">
          <div className="dc-history-title">
            <HistoryOutlined />
            脱敏任务
            <span className="dc-history-badge">保留 7 天</span>
          </div>
        </div>
        <Table
          rowKey="id"
          loading={listLoading}
          dataSource={jobs}
          locale={{ emptyText: '暂无脱敏任务' }}
          pagination={tablePagination(page, total, handlePaginationChange, paginationOptions)}
          columns={[
            { title: 'ID', dataIndex: 'id', width: 72 },
            { title: '文件名', dataIndex: 'file_name', ellipsis: true },
            {
              title: '状态',
              dataIndex: 'status',
              width: 100,
              render: (s: string) => <Tag color={STATUS_COLOR[s] || 'default'}>{STATUS_LABEL[s] || s}</Tag>,
            },
            {
              title: '进度',
              dataIndex: 'progress',
              width: 200,
              render: (p: number, row: JobItem) =>
                row.status === 'running' || row.status === 'pending' ? (
                  <Progress percent={p} size="small" status="active" />
                ) : row.status === 'error' ? (
                  <span style={{ color: 'var(--ant-color-error)' }}>{row.status_message || '失败'}</span>
                ) : (
                  '—'
                ),
            },
            { title: '命中', dataIndex: 'hit_count', width: 72 },
            {
              title: '创建时间',
              dataIndex: 'created_at',
              width: 168,
              render: (v: string | null) => formatDate(v),
            },
            {
              title: '操作',
              width: 96,
              align: 'center',
              render: (_: unknown, row: JobItem) => {
                const finished = row.status === 'completed' || row.status === 'error'
                return (
                  <div className="dc-history-actions">
                    <Button
                      type="link"
                      size="small"
                      icon={<EyeOutlined />}
                      title="查看"
                      disabled={row.status !== 'completed'}
                      onClick={() => openJob(row)}
                    />
                    {finished ? (
                      <Popconfirm
                        title="确定删除这条脱敏任务？"
                        description="删除后无法恢复；若临时上传未被其它任务引用，将一并清理。"
                        okText="删除"
                        cancelText="取消"
                        okButtonProps={{ danger: true, loading: deletingJobId === row.id }}
                        onConfirm={() => deleteJob(row.id)}
                      >
                        <Button
                          type="link"
                          size="small"
                          danger
                          icon={<DeleteOutlined />}
                          title="删除"
                          loading={deletingJobId === row.id}
                        />
                      </Popconfirm>
                    ) : null}
                  </div>
                )
              },
            },
          ]}
        />
      </div>

      <Modal
        className="doc-compare-modal doc-desensitize-result-modal"
        title={viewJob ? `脱敏结果 · ${viewJob.file_name}` : '脱敏结果'}
        open={!!showResult}
        onCancel={backFromViewer}
        footer={null}
        width="100%"
        destroyOnClose
        centered={false}
        style={{ top: 0, paddingBottom: 0, maxWidth: '100vw' }}
        styles={{
          root: { maxWidth: '100vw' },
          container: { height: '100vh', borderRadius: 0, display: 'flex', flexDirection: 'column' },
          body: { flex: 1, padding: 0, overflow: 'hidden', minHeight: 0 },
        }}
      >
        {showResult && viewJob ? (
          <DocDesensitizeViewer
            jobId={viewJob.id}
            fileName={viewJob.file_name}
            hitCount={viewJob.hit_count ?? viewJob.result?.hit_count ?? 0}
            truncated={viewJob.result?.truncated}
            onBack={backFromViewer}
          />
        ) : null}
      </Modal>
    </div>
  )
}
